/**
 * Regelversion 9: Gilt ein Gesetz schon, wird ein neuer Entwurf derselben Vorlage zur Novelle.
 *
 * Bis Version 8 ließ sich dasselbe Gesetz beliebig oft beschließen, jedes Mal mit voller Wirkung
 * (Bonus bei den Wählergruppen, weniger Problemdruck, Kosten). Jetzt:
 *  - Der Entwurf startet mit den Einstellungen des geltenden Gesetzes.
 *  - Die Novelle wirkt nur mit dem Unterschied zum geltenden Gesetz. Gleiche Einstellungen bringen nichts
 *    und lassen sich gar nicht erst beschließen.
 *  - Zum Nachschärfen dürfen Regler um die Hälfte ihres Bereichs über das bisherige Maximum hinaus.
 *  - Beim Öffnen eines Problems schlägt das Ministerium zuerst einen Ansatz vor, der noch nicht gilt.
 * Steuerreform und Standortpaket (Version 8) sind ausgenommen, sie wirken ohnehin als Änderung.
 */
import { S } from './state';
import { since } from './version';
import { TPL } from '../content/issues';
import { ISS } from '../content/states';
import { lawFx, FIN_KEY } from './v2';
import type { Law } from './types';

export const novOn = (): boolean => since(9);

const exempt = (iss: string): boolean => !iss || iss.startsWith('ag8_');

/** Das geltende Gesetz derselben Vorlage (das jüngste, falls es schon Novellen gibt). */
export function baseLaw(iss: string, tpl: number, exceptId?: number | null): Law | null {
  if (!novOn() || exempt(iss)) return null;
  const T = TPL(iss, tpl || 0); if (!T || T.custom) return null;
  let b: Law | null = null;
  for (const L of S.laws || []) {
    if (L.id === exceptId || L.iss !== iss || (L.tpl || 0) !== (tpl || 0) || L.rep || L.bestand) continue;
    if (L.stage !== 'kraft' && L.stage !== 'done') continue;
    if (!b || L.id > b.id) b = L;
  }
  return b;
}

/** Felder, die nicht als Unterschied gerechnet werden. `pos` bleibt vollständig: Abgestimmt wird über das Gesetz in der neuen Fassung. */
const KEEP = new Set(['months', 'gg', 'cut', 'fin', 'pos', 'richY', 'untY', 'arbY']);

/** Wirkung der Novelle = neue Fassung minus geltende Fassung. */
export function deltaFx(nf: any, of: any): any {
  const out: any = {};
  for (const k of new Set([...Object.keys(nf || {}), ...Object.keys(of || {})])) {
    const a = nf?.[k], b = of?.[k];
    if (KEEP.has(k)) { if (a !== undefined) out[k] = a; continue; }
    if (typeof a === 'number' || typeof b === 'number') { const d = (typeof a === 'number' ? a : 0) - (typeof b === 'number' ? b : 0); if (Math.abs(d) > 1e-9) out[k] = d; continue; }
    if ((a && typeof a === 'object') || (b && typeof b === 'object')) {
      const o: any = {};
      for (const j of new Set([...Object.keys(a || {}), ...Object.keys(b || {})])) {
        const x = a?.[j], y = b?.[j];
        if (typeof x === 'number' || typeof y === 'number') { const d = (+x || 0) - (+y || 0); if (Math.abs(d) > 1e-9) o[j] = d; }
        else if (x !== undefined) o[j] = x;
      }
      out[k] = o; continue;
    }
    if (a !== undefined) out[k] = a;
  }
  out.b ??= 0; out.sev ??= 0; out.g ??= {}; out.cut = Math.max(nf?.cut || 0, of?.cut || 0);
  return out;
}

const sameP = (a: any, b: any): boolean => {
  const k = new Set([...Object.keys(a || {}), ...Object.keys(b || {})].filter((x) => x !== 'title'));
  for (const x of k) { const u = a?.[x] ?? (x === FIN_KEY ? 0 : undefined), v = b?.[x] ?? (x === FIN_KEY ? 0 : undefined); if (Math.abs((+u || 0) - (+v || 0)) > 1e-9 || (typeof u === 'object' && JSON.stringify(u) !== JSON.stringify(v))) return false; }
  return true;
};

/** Wirkung eines Entwurfs nach den Regeln der Partie, als Novelle, wenn das Gesetz schon gilt. */
export function draftFx(T: any, iss: string, tpl: number, p: any, lawId?: number | null): any {
  const nf = lawFx(T, p, lawId), B = baseLaw(iss, tpl, lawId);
  if (!B) return nf;
  return { ...deltaFx(nf, lawFx(T, B.p || {}, B.id)), nov: B.id };
}

/** Warum sich der Entwurf nicht beschließen lässt (nur Novellen ohne Änderung). */
export function novBlock(iss: string, tpl: number, p: any, lawId?: number | null): string | null {
  const B = baseLaw(iss, tpl, lawId); if (!B) return null;
  if (sameP(p, B.p)) return `„${B.t}“ gilt bereits genau so. Schärft mindestens eine Stellschraube nach oder wählt einen anderen Ansatz.`;
  return null;
}

/** Höchstwert eines Reglers: In einer Novelle darf man um die Hälfte des Bereichs nachschärfen. */
export function paramMax(iss: string, tpl: number, pa: any, lawId?: number | null): number {
  if (pa.k !== 'r' || !baseLaw(iss, tpl, lawId)) return pa.max;
  const ext = (pa.max - pa.min) * .5, st = pa.st || 1;
  return Math.round((pa.max + ext) / st) * st;
}

/** Startwerte eines Entwurfs: bei geltendem Gesetz dessen Einstellungen. */
export function novStart(iss: string, tpl: number, defaults: any): any {
  const B = baseLaw(iss, tpl);
  return B ? { ...defaults, ...JSON.parse(JSON.stringify(B.p || {})) } : defaults;
}

/** Erster Ansatz für ein Problem: bevorzugt einer, der noch nicht gilt. */
export function novFirst(iss: string, ok: (T: any) => boolean, fallback: number): number {
  if (!novOn()) return fallback;
  const laws = ISS[iss]?.laws || [];
  const k = laws.findIndex((T: any, i: number) => ok(T) && !T.custom && !baseLaw(iss, i));
  return k >= 0 ? k : fallback;
}

/**
 * Version 9: Ist eine Vorlage in dieser Partie verfügbar? Neue Vorlagen (`v: 9`) nur ab Version 9,
 * Aufbaugesetze (`req`) erst, wenn zu dem Problem schon ein Gesetz gilt.
 */
export function tplAvail(T: any): boolean {
  if (!T || !T.v) return true;
  if (!since(T.v)) return false;
  if (!T.req) return true;
  return (S.laws || []).some((L: any) => L.iss === T.iss && !L.rep && !L.bestand && (L.stage === 'kraft' || L.stage === 'done'));
}

/** Gilt eine Vorlage schon? (Für die Auswahl im Entwurf.) */
export const tplInForce = (iss: string, tpl: number): boolean => !!baseLaw(iss, tpl);
